
import React, { useEffect, useRef, useCallback } from 'react';
import { Loader } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface InfiniteScrollLoaderProps {
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  threshold?: number;
  className?: string;
}

export function InfiniteScrollLoader({
  hasMore,
  isLoadingMore,
  onLoadMore,
  threshold = 800,
  className = ''
}: InfiniteScrollLoaderProps) {
  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastCallTimeRef = useRef<number>(0);
  const isProcessingRef = useRef<boolean>(false);
  const onLoadMoreRef = useRef(onLoadMore);

  // Aggiorna il ref quando onLoadMore cambia, ma non ricrea il callback
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  // Callback stabile che non cambia mai
  const stableDebouncedLoadMore = useCallback(() => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTimeRef.current;
    
    // Debounce di 1000ms tra le chiamate
    if (timeSinceLastCall < 1000) {
      console.log('🚫 InfiniteScrollLoader: Debounced - too soon since last call');
      return;
    }

    // Prevenire chiamate multiple simultanee
    if (isProcessingRef.current) {
      console.log('🚫 InfiniteScrollLoader: Already processing request');
      return;
    }

    console.log('🔥 InfiniteScrollLoader: CALLING onLoadMore (debounced)');
    lastCallTimeRef.current = now;
    isProcessingRef.current = true;
    
    try {
      onLoadMoreRef.current();
    } finally {
      // Reset processing flag dopo un delay
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    }
  }, []); // Dipendenze vuote - callback completamente stabile

  useEffect(() => {
    const loaderElement = loaderRef.current;
    
    // Guard conditions più robuste
    if (!loaderElement || !hasMore || isLoadingMore) {
      console.log("🚫 InfiniteScrollLoader: Skipping observer setup", {
        hasElement: !!loaderElement,
        hasMore,
        isLoadingMore
      });
      return;
    }

    console.log("🔧 InfiniteScrollLoader: Setting up observer", {
      hasMore,
      isLoadingMore,
      threshold
    });

    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new intersection observer con configurazione ottimizzata
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        console.log("👁️ InfiniteScrollLoader: Intersection event", {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio
        });
        
        // Condizioni più stringenti per il trigger
        if (entry.isIntersecting && 
            entry.intersectionRatio > 0 && 
            !isProcessingRef.current) {
          stableDebouncedLoadMore();
        }
      },
      {
        root: null,
        rootMargin: `${threshold}px`,
        threshold: [0, 0.1, 0.5]
      }
    );

    observerRef.current.observe(loaderElement);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, threshold, stableDebouncedLoadMore]);

  // Reset processing flag quando isLoadingMore cambia
  useEffect(() => {
    if (!isLoadingMore) {
      isProcessingRef.current = false;
    }
  }, [isLoadingMore]);

  if (!hasMore) {
    return (
      <div className={`text-center py-8 text-muted-foreground w-full ${className}`}>
        <p className="text-sm">
          ✅ Tutti i compro oro sono stati caricati
        </p>
      </div>
    );
  }

  return (
    <div 
      ref={loaderRef} 
      className={`flex flex-col items-center justify-center py-8 w-full ${className}`}
    >
      {isLoadingMore ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Loader className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">
              Caricamento compro oro...
            </span>
          </div>
          
          {/* Skeleton loading per i prossimi dealer - a tutta larghezza */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-3">
                <Skeleton className="h-48 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-muted border-t-primary rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Scorri per caricare altri compro oro
          </p>
        </div>
      )}
    </div>
  );
}
